import type { TypedDocumentNode } from '@apollo/client';
import { gql, useSuspenseQuery } from '@apollo/client';

import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { sportToBlogSportMap } from '@sorare/core/src/lib/routing';

import { supportedLanguages } from 'constants/types';
import { client } from 'lib/graphql';

import type {
  LatestBlogPostsQuery,
  LatestBlogPostsQueryVariables,
} from './__generated__/useLatestPosts.graphql';

const LATEST_POSTS_QUERY = gql`
  query LatestBlogPostsQuery(
    $first: Int!
    $categoryName: String
    $langTag: String
  ) {
    posts(
      first: $first
      where: { categoryName: $categoryName, tag: $langTag }
    ) {
      nodes {
        slug
        title
        date
        featured {
          featured
        }
        featuredImage {
          node {
            thumbnail: sourceUrl(size: MEDIUM)
          }
        }
      }
    }
  }
` as TypedDocumentNode<LatestBlogPostsQuery, LatestBlogPostsQueryVariables>;

export const useLatestPosts = (first: number = 10) => {
  const { sport } = useSportContext();

  const { language } = useIntlContext();

  const languageTag = supportedLanguages.has(language)
    ? `lang-${language}`
    : null;
  const { data } = useSuspenseQuery(LATEST_POSTS_QUERY, {
    client,
    variables: {
      first: first + 10,
      categoryName: sportToBlogSportMap[sport],
      langTag: languageTag,
    },
  });

  const sortedPosts = [...(data?.posts?.nodes || [])].sort((a, b) => {
    if (a.featured?.featured === 'true') {
      if (b.featured?.featured !== 'true') return -1;
      return b.date!.localeCompare(a.date!);
    }
    if (b.featured?.featured === 'true') return 1;
    return b.date!.localeCompare(a.date!);
  });

  return {
    posts: sortedPosts.slice(0, first),
  };
};
